import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { CssBaseline, useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from './index';
import { useTenant } from '../tenancy/TenantProvider';

const STORAGE_KEY = 'hr-system-color-mode';

const ThemeModeContext = createContext({
  mode: 'system',
  resolvedMode: 'light',
  setMode: () => {},
  toggleMode: () => {},
  reduceMotion: false,
});

export function AppThemeProvider({ children }) {
  const { branding } = useTenant();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });

  // Default to light mode for new sessions. Previously the app fell back to
  // 'system', which inherited the OS preference and surprised users on
  // dark-mode laptops with a dark UI before they'd ever opened a setting.
  // An explicit user choice (saved to localStorage) still wins.
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const resolvedMode = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  const theme = useMemo(() => createAppTheme(resolvedMode, branding), [branding, resolvedMode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const current = prev === 'system' ? (prefersDark ? 'dark' : 'light') : prev;
      return current === 'dark' ? 'light' : 'dark';
    });
  }, [prefersDark]);

  const contextValue = useMemo(
    () => ({ mode, resolvedMode, setMode, toggleMode, reduceMotion }),
    [mode, resolvedMode, reduceMotion, toggleMode]
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

